/**
 * Report-Derived eXtension (RDX-2) — per-report-type SED mapping registry.
 *
 * Declares which sections of a generated report contribute findings back
 * to the owning site's SED, and the dot-path each finding lands at.
 * `applyReportToSed` (RDX-3) iterates this registry post-`generateTypedReport`
 * and writes one `ReportDerivedClaim` per non-null extractor output.
 *
 * Wave-1 scope: only Feasibility is mapped — the user's primary failure
 * mode is "Feasibility concludes 145 MW, next Environmental run starts
 * from the 50-200 MW bucket". Wave-2 will extend the registry to the
 * other five report types (environmental, test-fit, power-availability,
 * water-resource, interconnection).
 *
 * Design rules:
 *  1. Extractors are PURE and TOTAL. Missing fields return `undefined`
 *     and the writer skips them. They must never throw.
 *  2. Extractors are DEFENSIVE — every property access guards for the
 *     real shape of a `SiteRagReport`, which is union-typed and varies
 *     across report types / generation modes.
 *  3. Adding a new report type without a registry entry is SAFE — the
 *     writer treats a missing entry as "no SED mutation".
 *  4. `appliesOver` documents the conflict-resolution intent for the
 *     Wave-2 resolver. Wave-1 only stores claims; it does not consume
 *     `appliesOver`. Listing a tier here is a forward-looking contract.
 */

import type { ReportTypeId } from '../../config/reportTypes';
import type { SiteRagReport } from '../../types/siteRag';

/**
 * Conflict-resolution intent for a single report→SED mapping.
 *
 *  - `'provider'` — when a provider-only slot exists, the report value
 *    is preferred (Wave-2 resolver will adopt). Use this for fields the
 *    report knows MORE precisely than the provider (e.g. exact MW vs.
 *    bucketed range).
 *  - `'doc'`      — also wins over uploaded-doc evidence. Conservative;
 *    use only when the report's finding is structurally more
 *    authoritative (rare).
 *  - `'authored'` — also wins over user-authored claims. Very rare; the
 *    user's typed claim should normally remain authoritative. Listed
 *    here for completeness but Wave-2 will likely refuse to ever
 *    write this tier.
 */
export type ReportTier = 'provider' | 'doc' | 'authored';

/**
 * A single field-level mapping from a generated report to an SED slot.
 * `applyReportToSed` ingests one `ReportDerivedClaim` per mapping whose
 * `extractor` returns a non-null value.
 */
export interface ReportFieldMapping {
  /**
   * Stable id of the section in the report the mapping is derived from
   * (e.g. `'final-recommendation'`, `'water-stress'`). Surfaced in the
   * UI lineage trail. Free-form string — not validated against the
   * report's actual section ids, since those are template-driven.
   */
  sourceSectionId: string;

  /**
   * Pure, total extractor. Returns the SED-bound value, or
   * `undefined`/`null` to signal "no finding for this mapping in this
   * particular report run".
   *
   * MUST NOT throw. MUST NOT mutate the report.
   */
  extractor: (report: SiteRagReport) => unknown;

  /**
   * Dot-path the value lands at within the SED. Format:
   * `<sectionId>.<field>` (e.g. `'grid.estimatedCapacityMW'`). The
   * resolver (Wave-2 RDX-4) matches claims to slots by this path.
   */
  sedFieldPath: string;

  /**
   * Optional confidence transform — maps the source report's
   * `evidenceConfidence.cap` to the claim's stored confidence. Default
   * is identity (claim inherits the report's cap, or 0.7 when the
   * report pre-dates `evidenceConfidence`).
   */
  confidenceTransform?: (raw: number) => number;

  /**
   * Tiers this mapping's finding is expected to win over (Wave-2
   * resolver gate). Wave-1 stores this on the registry only; the
   * writer ignores it.
   */
  appliesOver?: ReadonlyArray<ReportTier>;
}

/**
 * Defensive extractor — returns `undefined` for any non-finite or
 * NaN number, so a malformed report can't write nonsense into SED.
 */
function safeNumber(v: unknown): number | undefined {
  if (typeof v !== 'number') return undefined;
  if (!Number.isFinite(v)) return undefined;
  return v;
}

/**
 * Defensive extractor — returns `undefined` for empty / blank strings.
 */
function safeString(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * The headline capacity (MW) a Feasibility report concluded. Reads
 * defensively from the only places the SiteRagReport schema actually
 * carries an exact-MW signal:
 *
 *  - `report.datasets?.grid` / similar typed datasets, if extended;
 *  - the LLM-extracted `conclusion.overallScore` is NOT a capacity;
 *  - today the report doesn't have a typed `capacityMW` slot at the
 *    top level, so Wave-1's extractor is a stub that returns
 *    `undefined` unless a downstream change populates a known field.
 *
 * The extractor is wired here so the resolver path is testable end-
 * to-end; the registry can be extended without changing the writer.
 */
function feasibilityCapacityMW(report: SiteRagReport): number | undefined {
  // Common downstream sources where Feasibility's MW finding lands:
  // 1. The report's own `rerunConfig.typedPreferences.feasibility.projectSizeMW`
  //    — captures the user's stated ask, NOT a Feasibility-derived
  //    finding, so we skip it.
  // 2. `report.datasets?.grid` doesn't carry an MW field today; future
  //    schema extensions can add one.
  // 3. The LLM-emitted verdict / conclusion blocks are prose; an
  //    extraction pass would parse them. Deferred.
  //
  // The defensive contract: return `undefined` when no structured MW
  // is reachable. The writer skips the mapping silently.
  const r = report as unknown as {
    metrics?: { capacityMW?: unknown };
    verdict?: { capacityMW?: unknown };
  };
  return safeNumber(r.metrics?.capacityMW) ?? safeNumber(r.verdict?.capacityMW);
}

/**
 * Headline water-stress verdict. Today only `report.datasets?.waterStress`
 * is structured; the LLM narrative is prose. Defensive — `undefined`
 * when nothing is reachable.
 */
function feasibilityWaterStressVerdict(report: SiteRagReport): string | undefined {
  const r = report as unknown as {
    metrics?: { waterStressVerdict?: unknown };
    verdict?: { waterStressVerdict?: unknown };
  };
  return safeString(r.metrics?.waterStressVerdict) ?? safeString(r.verdict?.waterStressVerdict);
}

/**
 * Headline grid-interconnection status (e.g. `'Cleared'` /
 * `'Conditional'` / `'Queued'`). Same defensive shape.
 */
function feasibilityGridInterconnection(report: SiteRagReport): string | undefined {
  const r = report as unknown as {
    metrics?: { gridInterconnectionStatus?: unknown };
    verdict?: { gridInterconnectionStatus?: unknown };
  };
  return (
    safeString(r.metrics?.gridInterconnectionStatus) ??
    safeString(r.verdict?.gridInterconnectionStatus)
  );
}

/**
 * Headline Proceed / Proceed-with-conditions / Do-not-proceed verdict.
 * The report carries this on `conclusion.recommendation`; the registry
 * surfaces it as a slotless lineage anchor (no SED field today). Stored
 * because the resolver's debug logging in Wave-2 wants the top-line
 * verdict in scope for "Capacity 145 MW (Feasibility v3 - Proceed)".
 */
function feasibilityRecommendation(report: SiteRagReport): string | undefined {
  return safeString(report.conclusion?.recommendation);
}

/* ── Wave-2b — Report-type-specific extractors ─────────────────────── */

/**
 * Defensive extractor for any report's overall conclusion recommendation.
 * Mirrors `feasibilityRecommendation` but scoped per report type so the
 * registry entries can stamp typed lineage in the SED's
 * `siteResearch.<type>Recommendation` slot.
 */
function genericRecommendation(report: SiteRagReport): string | undefined {
  return safeString(report.conclusion?.recommendation);
}

/**
 * Environmental — NEPA pathway. Today the report shape doesn't expose a
 * typed `recommendedNepaPathway` field on the top level. Defensive
 * extractor reads two candidate locations and returns `undefined` when
 * nothing is reachable. Future schema work can populate either slot.
 */
function envNepaPathway(report: SiteRagReport): string | undefined {
  const r = report as unknown as {
    metrics?: { recommendedNepaPathway?: unknown };
    verdict?: { recommendedNepaPathway?: unknown };
  };
  return safeString(r.metrics?.recommendedNepaPathway) ?? safeString(r.verdict?.recommendedNepaPathway);
}

/**
 * Environmental — threatened-species count. Numeric finding the EIA
 * pipeline lands on. Defensive; returns undefined when absent so the
 * writer skips the mapping.
 */
function envSpeciesCount(report: SiteRagReport): number | undefined {
  const r = report as unknown as {
    metrics?: { threatenedSpeciesCount?: unknown };
    verdict?: { threatenedSpeciesCount?: unknown };
  };
  return safeNumber(r.metrics?.threatenedSpeciesCount) ?? safeNumber(r.verdict?.threatenedSpeciesCount);
}

/**
 * Test-Fit — recommended building footprint area (sqft / m²; the writer
 * stores the raw number, units stay in the source section's text).
 * Defensive — undefined when nothing is reachable.
 */
function testFitFootprintArea(report: SiteRagReport): number | undefined {
  const r = report as unknown as {
    metrics?: { recommendedFootprintArea?: unknown };
    verdict?: { recommendedFootprintArea?: unknown };
  };
  return safeNumber(r.metrics?.recommendedFootprintArea) ?? safeNumber(r.verdict?.recommendedFootprintArea);
}

/**
 * Power-Availability — concluded interconnection capacity (MW). May
 * be tighter than Feasibility's headline if the queue scan turned up a
 * binding constraint. Defensive — undefined when absent.
 */
function powerAvailCapacityMW(report: SiteRagReport): number | undefined {
  const r = report as unknown as {
    metrics?: { availableCapacityMW?: unknown; powerAvailableMW?: unknown };
    verdict?: { availableCapacityMW?: unknown };
  };
  return (
    safeNumber(r.metrics?.availableCapacityMW)
    ?? safeNumber(r.metrics?.powerAvailableMW)
    ?? safeNumber(r.verdict?.availableCapacityMW)
  );
}

/**
 * Power-Availability — estimated time-to-power (months from study).
 * The Feasibility / Power-Availability report types carry a numeric
 * estimate the writer ingests into `grid.estimatedQueueTime`.
 */
function powerAvailTimeToPower(report: SiteRagReport): number | undefined {
  const r = report as unknown as {
    metrics?: { timeToPowerMonths?: unknown; estimatedQueueTimeMonths?: unknown };
    verdict?: { timeToPowerMonths?: unknown };
  };
  return (
    safeNumber(r.metrics?.timeToPowerMonths)
    ?? safeNumber(r.metrics?.estimatedQueueTimeMonths)
    ?? safeNumber(r.verdict?.timeToPowerMonths)
  );
}

/**
 * Water-Resource — concluded water-availability verdict. Same shape as
 * Feasibility's water-stress headline but a Water-Resource study carries
 * a more granular conclusion (e.g. groundwater + municipal + drought).
 */
function waterResourceVerdict(report: SiteRagReport): string | undefined {
  const r = report as unknown as {
    metrics?: { waterAvailabilityVerdict?: unknown; waterResourceVerdict?: unknown };
    verdict?: { waterAvailabilityVerdict?: unknown };
  };
  return (
    safeString(r.metrics?.waterAvailabilityVerdict)
    ?? safeString(r.metrics?.waterResourceVerdict)
    ?? safeString(r.verdict?.waterAvailabilityVerdict)
  );
}

/**
 * Interconnection — recommended carrier / route summary. Free-form
 * string; the writer treats this as a lineage anchor.
 */
function interconnectionRecommendation(report: SiteRagReport): string | undefined {
  const r = report as unknown as {
    metrics?: { recommendedCarrier?: unknown };
    verdict?: { recommendedCarrier?: unknown };
  };
  return safeString(r.metrics?.recommendedCarrier) ?? safeString(r.verdict?.recommendedCarrier);
}

/**
 * Per-report-type mapping table.
 *
 * Wave-1 shipped Feasibility only. Wave-2b extends to the other five
 * report types. Each entry uses defensive extractors that return
 * `undefined` when the report's typed payload doesn't carry the field
 * — the SAFE default. The extractors collectively guarantee:
 *
 *  1. Adding a new report type without an entry → no SED mutation.
 *  2. Adding a registry entry whose extractor returns undefined on
 *     every real report → no SED mutation.
 *  3. Adding a registry entry whose extractor returns a real value →
 *     one ReportDerivedClaim per `(reportId, sedFieldPath)`, idempotent.
 *
 * The lineage anchor (`siteResearch.<type>Recommendation`) is the
 * minimum every report type carries — every report has a
 * `conclusion.recommendation` string. This guarantees the writer fires
 * for every report type even when typed metrics aren't present yet,
 * so the resolver / UI surfaces see lineage from day one.
 */
export const REPORT_TO_SED_MAPPING: Partial<Record<ReportTypeId, ReadonlyArray<ReportFieldMapping>>> = {
  feasibility: [
    {
      sourceSectionId: 'final-recommendation',
      extractor: feasibilityCapacityMW,
      sedFieldPath: 'grid.estimatedCapacityMW',
      appliesOver: ['provider'],
    },
    {
      sourceSectionId: 'water-stress',
      extractor: feasibilityWaterStressVerdict,
      sedFieldPath: 'waterStress.stressLevel',
      appliesOver: ['provider'],
    },
    {
      sourceSectionId: 'final-recommendation',
      extractor: feasibilityGridInterconnection,
      sedFieldPath: 'grid.interconnectionStatus',
      appliesOver: ['provider', 'doc'],
    },
    {
      sourceSectionId: 'final-recommendation',
      extractor: feasibilityRecommendation,
      sedFieldPath: 'siteResearch.feasibilityRecommendation',
      appliesOver: ['provider'],
    },
  ],
  environmental: [
    {
      sourceSectionId: 'nepa-pathway',
      extractor: envNepaPathway,
      sedFieldPath: 'environmental.recommendedNepaPathway',
      appliesOver: ['provider'],
    },
    {
      sourceSectionId: 'species-concerns',
      extractor: envSpeciesCount,
      sedFieldPath: 'environmental.speciesCount',
      appliesOver: ['provider'],
    },
    {
      sourceSectionId: 'final-recommendation',
      extractor: genericRecommendation,
      sedFieldPath: 'siteResearch.environmentalRecommendation',
      appliesOver: ['provider'],
    },
  ],
  'test-fit': [
    {
      sourceSectionId: 'building-placement',
      extractor: testFitFootprintArea,
      sedFieldPath: 'parcel.recommendedFootprintArea',
      appliesOver: ['provider'],
    },
    {
      sourceSectionId: 'final-recommendation',
      extractor: genericRecommendation,
      sedFieldPath: 'siteResearch.testFitRecommendation',
      appliesOver: ['provider'],
    },
  ],
  'power-availability': [
    {
      sourceSectionId: 'capacity-analysis',
      extractor: powerAvailCapacityMW,
      sedFieldPath: 'grid.availableCapacityMW',
      appliesOver: ['provider'],
    },
    {
      sourceSectionId: 'queue-position',
      extractor: powerAvailTimeToPower,
      sedFieldPath: 'grid.estimatedQueueTime',
      appliesOver: ['provider'],
    },
    {
      sourceSectionId: 'final-recommendation',
      extractor: genericRecommendation,
      sedFieldPath: 'siteResearch.powerAvailabilityRecommendation',
      appliesOver: ['provider'],
    },
  ],
  'water-resource': [
    {
      sourceSectionId: 'availability',
      extractor: waterResourceVerdict,
      sedFieldPath: 'waterStress.resourceVerdict',
      appliesOver: ['provider'],
    },
    {
      sourceSectionId: 'final-recommendation',
      extractor: genericRecommendation,
      sedFieldPath: 'siteResearch.waterResourceRecommendation',
      appliesOver: ['provider'],
    },
  ],
  interconnection: [
    {
      sourceSectionId: 'carrier-analysis',
      extractor: interconnectionRecommendation,
      sedFieldPath: 'connectivity.recommendedCarrier',
      appliesOver: ['provider'],
    },
    {
      sourceSectionId: 'final-recommendation',
      extractor: genericRecommendation,
      sedFieldPath: 'siteResearch.interconnectionRecommendation',
      appliesOver: ['provider'],
    },
  ],
};

/**
 * Lookup helper. Returns `[]` when the report type has no mapping
 * registered — the safe default that keeps Wave-2 ramp-up incremental.
 */
export function getReportMapping(reportType: ReportTypeId): ReadonlyArray<ReportFieldMapping> {
  return REPORT_TO_SED_MAPPING[reportType] ?? [];
}
